import React, { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { FeatureFlag } from 'constants/featureFlags';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import { getConditionalArray } from 'utils/data';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import {
  getChattelDetailsAction,
  getLoanCicFeeReasonsAction,
  getLoanCicFeeReasonsActionV2,
  getLoanFeeCreditsAction,
  getLoanFeeReasonHistoryAction,
  getLoanFeeRecommendationsAction,
  getLoanFeesViewAction,
  getLoanImpoundsAction,
  getRedesignedLoanFeesAction,
} from 'store/loanApplications/actions';
import {
  selectIsChattelLoanPurposeFromApplication,
  selectIsLoanChangesNotSaved,
} from 'store/loanApplications/selectors';
import {
  getCicFeeReasonsFilteredAction,
  getFeeProvidersAction,
  getFeeTypeByCalculationTypeAction,
  getImpoundTypesAction,
} from 'store/system/actions';

import FeeRecommendationsPanel from './components/FeeRecommendationsPanel';
import { wrapperStyles } from './LoanFees.styles';
import LoanFeesBody from './LoanFeesBody';
import LoanFeesHead from './LoanFeesHead';

const LoanFees = ({ setIsLoading, isLoading }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();
  const loanId = useLoanId();

  const isSFCicReasonEnabledFF = useFeatureToggle(FeatureFlag.EnableStraightforwardCicReason);

  const [isError, setIsError] = useState(false);

  const isLoanChangesNotSaved = useSelector(selectIsLoanChangesNotSaved);
  const isChattel = useSelector(selectIsChattelLoanPurposeFromApplication);
  const isFeeRecommendationsReviewAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.CLOSING_PORTAL_MANAGE),
  );

  const isManageCicHistoryEnabled = useFeatureToggle(FeatureFlag.EnableManageCicHistory);

  const fetchData = useCallback(async () => {
    setIsError(false);
    setIsLoading(true);

    return Promise.all([
      !isSFCicReasonEnabledFF && dispatch(getLoanCicFeeReasonsAction({ loanId })),
      dispatch(getFeeProvidersAction()),
      dispatch(getLoanFeesViewAction({ applicationId })),
      dispatch(getImpoundTypesAction({ includeDeleted: true })),
      dispatch(getLoanImpoundsAction({ applicationId })),
      dispatch(getFeeTypeByCalculationTypeAction({ calculationType: null, includeDeleted: true })),
      isSFCicReasonEnabledFF && dispatch(getCicFeeReasonsFilteredAction({ applicationId })),
      isSFCicReasonEnabledFF && dispatch(getLoanCicFeeReasonsActionV2({ loanId })),

      // Because of BE issue (they cannot handle PUT correctly if FE sends only fees from section that were edited). Frontend needs to send all fees by way that we kept to on prev page
      dispatch(getRedesignedLoanFeesAction({ loanId })),
      dispatch(getLoanFeeCreditsAction({ loanId })),

      ...getConditionalArray(isManageCicHistoryEnabled, () => [
        dispatch(getLoanFeeReasonHistoryAction({ loanId })),
      ]),
    ])
      .catch(Error)
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    setIsLoading,
    applicationId,
    loanId,
    isLoanChangesNotSaved,
    isManageCicHistoryEnabled,
  ]);

  // Kept out of fetchData: a failed recommendations fetch must never short-circuit the tab's
  // Promise.all, and the review panel refreshes recommendations itself after each review.
  const fetchRecommendations = useCallback(
    () => dispatch(getLoanFeeRecommendationsAction({ loanId, silent: true })).catch(Error),
    [dispatch, loanId],
  );

  const fetchTabData = useCallback(() => {
    if (isFeeRecommendationsReviewAllowed) {
      fetchRecommendations();
    }

    return fetchData();
  }, [fetchData, fetchRecommendations, isFeeRecommendationsReviewAllowed]);

  useEffectSafeStrictMode(() => {
    fetchTabData();
  }, [fetchTabData]);

  // A chattel refinance lays its fees out as a refinance (LEN-205), which needs its purpose of chattel.
  useEffectSafeStrictMode(() => {
    if (isChattel) {
      dispatch(getChattelDetailsAction({ applicationId })).catch(Error);
    }
  }, [dispatch, isChattel, applicationId]);

  return (
    <ErrorHandlingLayout isError={isError} onClick={fetchTabData}>
      <Box sx={wrapperStyles}>
        {isFeeRecommendationsReviewAllowed && (
          <FeeRecommendationsPanel loanId={loanId} onReviewed={fetchData} />
        )}
        <LoanFeesHead />
        <LoanFeesBody isLoading={isLoading} setIsLoading={setIsLoading} />
      </Box>
    </ErrorHandlingLayout>
  );
};

LoanFees.propTypes = {
  ...WithLoadingPropTypes,
};

export default withLoading(memo(LoanFees));
